import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, MapPin, Ticket, Radio, ArrowRight, Minus, Plus, Flame, Share2 } from "lucide-react";
import { api, formatZAR, resolveUrl } from "../lib/api";
import { submitPayFast } from "../lib/payfast";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export function EventPosterCarousel({ event }) {
  const posters = useMemo(() => {
    const list = [event.image_url, ...(event.additional_images || [])]
      .filter((u) => typeof u === "string" && u.trim().length > 0);
    // De-dupe while preserving order.
    return Array.from(new Set(list));
  }, [event.image_url, event.additional_images]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (posters.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % posters.length), 4000);
    return () => clearInterval(t);
  }, [posters.length]);

  if (posters.length === 0) {
    return <div className="w-full h-full bg-gray-100" />;
  }

  return (
    <div className="relative w-full h-full bg-gray-100 overflow-hidden" data-testid={`poster-carousel-${event.id}`}>
      {posters.map((url, i) => (
        <img
          key={url}
          src={resolveUrl(url)}
          alt={event.title}
          loading={i === 0 ? "eager" : "lazy"}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out ${i === idx ? "opacity-100" : "opacity-0"}`}
        />
      ))}
      {posters.length > 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10" data-testid={`poster-dots-${event.id}`}>
          {posters.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={(e) => { e.stopPropagation(); setIdx(i); }}
              aria-label={`Show poster ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-white" : "w-1.5 bg-white/60 hover:bg-white/90"}`}
              data-testid={`poster-dot-${event.id}-${i}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function TierSelector({ event, onBuy, busy }) {
  const [tierId, setTierId] = useState(event.tiers?.[0]?.id || "");
  const [qty, setQty] = useState(1);
  const tier = event.tiers?.find((t) => t.id === tierId);
  const available = tier ? (tier.quantity || 0) - (tier.sold || 0) : 0;
  const total = tier ? tier.price * qty : 0;
  const showAvailability = event.show_availability !== false; // default true

  const tierScarcity = (t) => {
    const avail = (t.quantity || 0) - (t.sold || 0);
    const cap = t.quantity || 0;
    if (avail <= 0) return { label: "Sold out", level: "out" };
    if (!showAvailability) return { label: "", level: "hidden" };
    if (avail <= 10) return { label: `Only ${avail} left`, level: "critical" };
    if (cap > 0 && avail / cap <= 0.15) return { label: `Only ${avail} left`, level: "low" };
    if (avail < 50) return { label: `${avail} left`, level: "mid" };
    return { label: `${avail} available`, level: "ok" };
  };

  return (
    <div className="space-y-3" data-testid={`tier-selector-${event.id}`}>
      <div className="text-xs tracking-[0.2em] uppercase text-gray-500">Select tier</div>
      <div className="grid sm:grid-cols-3 gap-2">
        {event.tiers.map((t) => {
          const scarcity = tierScarcity(t);
          const soldOut = scarcity.level === "out";
          const selected = tierId === t.id;
          const badgeColor = selected
            ? "text-white/90"
            : scarcity.level === "critical" ? "text-[#C00000] font-semibold animate-pulse"
            : scarcity.level === "low" ? "text-[#C00000] font-semibold"
            : scarcity.level === "mid" ? "text-amber-700"
            : "text-gray-500";
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => { if (!soldOut) { setTierId(t.id); setQty(1); } }}
              disabled={soldOut}
              className={`relative p-3 border text-left transition-colors ${
                selected ? "border-[#1F3864] bg-[#1F3864] text-white" : "border-gray-300 hover:border-[#1F3864]"
              } ${soldOut ? "opacity-60 cursor-not-allowed" : ""}`}
              data-testid={`tier-option-${t.id}`}
            >
              {(scarcity.level === "critical" || scarcity.level === "low") && !selected && (
                <div className="absolute -top-2 -right-2 px-2 py-0.5 bg-[#C00000] text-white text-[9px] tracking-[0.15em] uppercase font-medium">
                  Almost gone
                </div>
              )}
              <div className={`text-xs tracking-[0.15em] uppercase mb-1 ${soldOut ? "line-through" : ""}`}>{t.name}</div>
              <div className={`font-display text-xl leading-none ${soldOut ? "line-through" : ""}`}>{formatZAR(t.price)}</div>
              {scarcity.label && (
                <div className={`text-[10px] mt-1 ${badgeColor}`} data-testid={`tier-scarcity-${t.id}`}>
                  {scarcity.label}
                </div>
              )}
              {showAvailability && !soldOut && t.quantity > 0 && (
                <div className={`mt-1.5 h-0.5 ${selected ? "bg-white/20" : "bg-gray-200"}`}>
                  <div
                    className={scarcity.level === "critical" ? "h-full bg-[#C00000]" : scarcity.level === "low" ? "h-full bg-[#C00000]/70" : selected ? "h-full bg-white/80" : "h-full bg-[#1F3864]/60"}
                    style={{ width: `${Math.min(100, Math.max(2, ((t.sold || 0) / t.quantity) * 100))}%` }}
                  />
                </div>
              )}
            </button>
          );
        })}
      </div>
      {tier && available > 0 && (
        <>
          <div className="flex items-center justify-between pt-1">
            <div className="text-xs tracking-[0.2em] uppercase text-gray-500">Quantity</div>
            <div className="flex items-center border border-gray-300">
              <button type="button" onClick={() => setQty(Math.max(1, qty - 1))} className="p-2 hover:bg-gray-100" data-testid={`qty-minus-${event.id}`}><Minus className="w-3 h-3" /></button>
              <div className="px-4 text-sm font-semibold min-w-[3rem] text-center" data-testid={`qty-value-${event.id}`}>{qty}</div>
              <button type="button" onClick={() => setQty(Math.min(10, available, qty + 1))} className="p-2 hover:bg-gray-100" data-testid={`qty-plus-${event.id}`}><Plus className="w-3 h-3" /></button>
            </div>
          </div>
          {available <= 20 && showAvailability && (
            <div className="flex items-center gap-2 text-xs text-[#C00000]" data-testid={`scarcity-banner-${event.id}`}>
              <Flame className="w-3.5 h-3.5" />
              <span className="font-semibold">Selling fast — only {available} {tier.name} ticket{available === 1 ? "" : "s"} left</span>
            </div>
          )}
          <div className="flex items-center justify-between pt-2 border-t border-gray-200">
            <div>
              <div className="text-xs tracking-[0.2em] uppercase text-gray-500">Total</div>
              <div className="font-display text-2xl text-[#1F3864]">{formatZAR(total)}</div>
            </div>
            <button onClick={() => onBuy(tier.id, qty)} disabled={busy} className="btn-primary-red disabled:opacity-60" data-testid={`action-buy-tickets-${event.id}`}>
              <Ticket className="w-4 h-4" /> {busy ? "Loading..." : `Buy ${qty} Ticket${qty > 1 ? "s" : ""}`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [tab, setTab] = useState("upcoming");
  const [busy, setBusy] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  const now = new Date();
  const upcoming = events.filter((e) => new Date(e.event_date) >= now);
  const past = events.filter((e) => new Date(e.event_date) < now);
  const shown = tab === "upcoming" ? upcoming : past;

  const buyTicket = async (e, tierId = null, quantity = 1) => {
    if (!user) {
      toast.error("Please sign in to purchase a ticket");
      navigate("/login?next=/events");
      return;
    }
    setBusy(e.id);
    try {
      const { data } = await api.post("/payments/payfast/event-ticket", {
        event_id: e.id,
        tier_id: tierId,
        quantity,
        origin_url: window.location.origin,
      });
      submitPayFast(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setBusy(null);
    }
  };

  return (
    <div>
      <section className="bg-[#0B1526] text-white py-24 sm:py-32 relative overflow-hidden" data-testid="events-hero">
        <img src="https://images.pexels.com/photos/14990434/pexels-photo-14990434.jpeg?auto=compress&cs=tinysrgb&w=1920" alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-4">Live & Events</div>
          <h1 className="font-display text-5xl sm:text-7xl leading-[0.95] max-w-4xl">
            Gather. Worship. <br /><span className="italic text-[#C00000]">Go beyond.</span>
          </h1>
          <p className="mt-6 text-white/70 max-w-xl text-lg">
            From stadium recordings to intimate livestreams — come be part of what God is doing.
          </p>
        </div>
      </section>

      <section className="py-12 border-b border-gray-200 sticky top-20 bg-white z-20" data-testid="events-tabs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-2">
          <button
            onClick={() => setTab("upcoming")}
            className={`px-5 py-2.5 text-xs tracking-[0.2em] uppercase border ${tab === "upcoming" ? "bg-[#1F3864] text-white border-[#1F3864]" : "border-gray-300 text-gray-700"}`}
            data-testid="tab-upcoming"
          >Upcoming</button>
          <button
            onClick={() => setTab("past")}
            className={`px-5 py-2.5 text-xs tracking-[0.2em] uppercase border ${tab === "past" ? "bg-[#1F3864] text-white border-[#1F3864]" : "border-gray-300 text-gray-700"}`}
            data-testid="tab-past"
          >Past Events</button>
        </div>
      </section>

      <section className="py-16 sm:py-20" data-testid="events-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          {shown.map((e) => (
            <div key={e.id} className="grid md:grid-cols-12 gap-6 border border-gray-200 bg-white hover:border-[#1F3864] transition-colors" data-testid={`event-row-${e.id}`}>
              <div className="md:col-span-4 aspect-[16/10] md:aspect-auto overflow-hidden bg-gray-100">
                <EventPosterCarousel event={e} />
              </div>
              <div className="md:col-span-8 p-6 md:p-8 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-4 text-[11px] tracking-[0.2em] uppercase text-[#C00000] mb-4 flex-wrap">
                    <span className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5" />{new Date(e.event_date).toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" })}</span>
                    {e.is_live_stream && <span className="flex items-center gap-2"><Radio className="w-3.5 h-3.5" />Live Stream</span>}
                    {(() => {
                      if (e.general_admission || !e.tiers?.length) return null;
                      if (e.show_availability === false) return null; // hidden per admin toggle
                      const totalAvail = e.tiers.reduce((s, t) => s + Math.max(0, (t.quantity || 0) - (t.sold || 0)), 0);
                      const totalCap = e.tiers.reduce((s, t) => s + (t.quantity || 0), 0);
                      if (totalAvail === 0) {
                        return <span className="flex items-center gap-2 text-gray-500"><Ticket className="w-3.5 h-3.5" />Sold out</span>;
                      }
                      if (totalCap > 0 && totalAvail / totalCap <= 0.2) {
                        return <span className="flex items-center gap-2 text-[#C00000] font-semibold animate-pulse"><Flame className="w-3.5 h-3.5" />Selling fast</span>;
                      }
                      return null;
                    })()}
                    {e.photos_published && (
                      <a href={`/events/${e.id}/photos`} className="flex items-center gap-2 text-[#1F3864] font-semibold hover:text-[#C00000] transition-colors" data-testid={`event-photos-link-${e.id}`}>
                        <ArrowRight className="w-3.5 h-3.5" />Photo Gallery
                      </a>
                    )}
                  </div>
                  <h3 className="font-display text-2xl sm:text-3xl text-[#1F3864] mb-3 leading-tight">
                    <a href={`/events/${e.id}`} className="hover:text-[#C00000] transition-colors" data-testid={`event-detail-link-${e.id}`}>{e.title}</a>
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-4">{e.description}</p>
                  <div className="flex items-center gap-4 flex-wrap text-sm text-gray-700">
                    <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#1F3864]" /> {e.venue}</span>
                    <button
                      type="button"
                      onClick={async (evt) => {
                        evt.stopPropagation();
                        const url = `${window.location.origin}/events/${e.id}`;
                        try {
                          if (navigator.share) {
                            await navigator.share({ title: e.title, text: e.description?.slice(0, 140), url });
                          } else {
                            await navigator.clipboard.writeText(url);
                            toast.success("Link copied");
                          }
                        } catch { /* user cancelled */ }
                      }}
                      className="flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase text-[#1F3864] hover:text-[#C00000] transition-colors"
                      data-testid={`event-share-btn-${e.id}`}
                    ><Share2 className="w-3.5 h-3.5" /> Share</button>
                  </div>
                </div>
                {tab === "upcoming" && (
                  <div className="mt-6 pt-5 border-t border-gray-100">
                    {(!e.general_admission && e.tiers && e.tiers.length > 0) ? (
                      <TierSelector event={e} onBuy={(tierId, qty) => buyTicket(e, tierId, qty)} busy={busy === e.id} />
                    ) : (
                      <div className="flex flex-wrap items-center gap-4 justify-between">
                        <div>
                          <div className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-1">Ticket</div>
                          <div className="font-display text-2xl text-[#1F3864]">
                            {e.ticket_price > 0 ? formatZAR(e.ticket_price) : "Free RSVP"}
                          </div>
                        </div>
                        {e.ticket_price > 0 ? (
                          <button onClick={() => buyTicket(e, null, 1)} disabled={busy === e.id} className="btn-primary-red disabled:opacity-60" data-testid={`action-buy-ticket-${e.id}`}>
                            <Ticket className="w-4 h-4" /> {busy === e.id ? "Loading..." : "Buy Ticket / Stream"}
                          </button>
                        ) : (
                          <button onClick={() => toast.success("RSVP received — see you there!")} className="btn-primary-navy" data-testid={`action-rsvp-${e.id}`}>
                            <ArrowRight className="w-4 h-4" /> RSVP
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
          {shown.length === 0 && <div className="py-20 text-center text-gray-500">No events in this category yet.</div>}
        </div>
      </section>
    </div>
  );
}
